"use client";

import Image from "next/image";
import AnimatedContent from "../AnimatedContent";
import { useTranslation } from "react-i18next";
import SectionContainer from "@/common/SectionContainer";

interface CompetitiveItem {
  id: number;
  title: string;
  beforeImg: string;
  afterImg: string;
  table: string[][];
}

const CompetitiveSection = () => {
  const { t } = useTranslation();
  const items = t("COMPETITIVE_SECTION_ITEMS", {
    returnObjects: true,
  }) as CompetitiveItem[];

  return (
    <SectionContainer
      id="competitive-section"
      className="section-shell bg-green-50 text-gray-900"
    >
      <div className="section-inner flex flex-col space-y-20 md:space-y-28">
        <div className="flex flex-col gap-4 md:gap-6 text-center mb-20 md:mb-28">
          <h2 className="section-title">{t("COMPETITIVE_SECTION_TITLE")}</h2>
          <p className="caption text-gray-600 whitespace-pre-wrap section-lead">
            {t("COMPETITIVE_SECTION_SUB_TITLE")}
          </p>
        </div>

        {items.map((item) => (
          <AnimatedContent key={item.id}>
            <div className="w-fit mx-auto bg-gradient-to-r from-[#5BBF4D]/80 to-[#207AE8]/80 text-white font-semibold text-[clamp(1.25rem,2.5vw,1.75rem)] py-1 px-4 mb-7 md:mb-14 rounded-xl shadow-sm text-center">
              {item.title}
            </div>

            <div className="flex flex-col lg:flex-row justify-center items-center gap-8">
              <div className="relative w-full lg:w-1/2 aspect-[16/9] rounded-2xl overflow-hidden shadow-md">
                <Image
                  src={item.beforeImg}
                  alt={t("COMPETITIVE_SECTION_BEFORE_ALT")}
                  fill
                  sizes="(max-width: 768px) 100vw, 75vw"
                  className="object-cover grayscale"
                  quality={90}
                />
              </div>

              <div className="rotate-90 lg:rotate-none flex items-center justify-center text-5xl text-transparent bg-clip-text bg-gradient-to-r from-[#5BBF4D] to-[#207AE8] font-extrabold">
                →
              </div>

              <div className="relative w-full lg:w-1/2 aspect-[16/9] rounded-2xl overflow-hidden shadow-md">
                <Image
                  src={item.afterImg}
                  alt={t("COMPETITIVE_SECTION_AFTER_ALT")}
                  fill
                  sizes="(max-width: 768px) 100vw, 50vw"
                  className="object-cover"
                  quality={90}
                />
              </div>
            </div>

            <div className="bg-gray-50 rounded-2xl shadow-inner border border-gray-100 overflow-hidden mt-16 md:mt-32">
              <table className="w-full border-collapse text-sm sm:text-base">
                <thead>
                  <tr className="bg-gradient-to-r from-[#5BBF4D]/20 to-[#207AE8]/20 text-gray-800">
                    <th className="py-4 px-6 text-center font-semibold w-1/3">
                      {t("COMPETITIVE_SECTION_TABLE_HEADER_1")}
                    </th>
                    <th className="py-4 px-6 text-center font-semibold w-1/3">
                      {t("COMPETITIVE_SECTION_TABLE_HEADER_2")}
                    </th>
                    <th className="py-4 px-6 text-center font-semibold w-1/3 text-[#207AE8]">
                      {t("COMPETITIVE_SECTION_TABLE_HEADER_3")}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {item.table.map((row, i) => (
                    <tr
                      key={row[0]}
                      className={`border-t border-gray-100 ${
                        i % 2 === 0 ? "bg-white" : "bg-gray-50"
                      }`}
                    >
                      {row.map((cell, j) => (
                        <td
                          key={`${row[0]}-${j}`}
                          className={`py-4 px-6 text-center ${
                            j < 2 ? "border-r border-gray-100" : ""
                          }`}
                        >
                          {cell}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </AnimatedContent>
        ))}
      </div>
    </SectionContainer>
  );
};

export default CompetitiveSection;
